/**
 * "What's new" — in-app feature announcements (VRG-221).
 *
 * Wire shapes shared between apps/api and apps/platform. Hand-written rather than
 * derived from the drizzle schema, matching the house convention that
 * `support.interface.ts` states: the API speaks camelCase and hides internal
 * columns, and coupling the client to the table shape is how a column rename
 * becomes a frontend break.
 *
 * Dates are `string` here (JSON), not `Date`. The API-side twin in
 * `apps/api/src/features/announcements/announcements.types.ts` uses `Date` — the
 * same deliberate two-copy split `support.types.ts` / `support.interface.ts` has.
 */

/**
 * What kind of change this is. Drives a fixed set of chip colours, which is why it
 * is a closed union and a Postgres enum rather than free text — a typo'd kind must
 * not be able to render an unstyled chip.
 */
export type AnnouncementKind =
  | 'feature'
  | 'improvement'
  | 'fix'
  | 'security'
  | 'deprecation';

/**
 * Order matters — it is the order the kinds appear in the internal editor's picker,
 * and it runs from the most common to the most consequential rather than
 * alphabetically.
 */
export const ANNOUNCEMENT_KINDS: AnnouncementKind[] = [
  'feature',
  'improvement',
  'fix',
  'security',
  'deprecation',
];

/**
 * `security` and `deprecation` were added after the first three (VRG-221), because
 * those three could only describe things GETTING BETTER. The two announcements a
 * brand most needs to distinguish from "New" are "something you depend on is
 * changing or going away" and "this concerned your data" — filed under `feature` or
 * `improvement` they read as optional reading, which for a deprecation is actively
 * misleading: the reader has to act, and a "New" chip says they do not.
 *
 * ## ALL FIVE ARE NOUNS (reversed deliberately, 2026-08-20)
 *
 * This comment used to say the opposite — *"'Deprecated', not 'Deprecation': every
 * other label is what happened to the thing, not the name of a category"* — and that
 * rule is retired, so do not restore it from a diff.
 *
 * It stopped describing the labels the moment `security` was added: `Feature` and
 * `Security` are nouns, `Improved` / `Fixed` / `Deprecated` were participles, and a
 * three-to-two split is the inconsistency the old rule existed to prevent, just
 * pointing the other way. Nouns win the tie because the chip names WHAT KIND OF
 * ANNOUNCEMENT this is, not what happened to the feature — it sits in the same slot
 * as `Security`, which no one reads as a participle-shaped hole.
 *
 * `Deprecation` was the one worth pausing on, since a deprecation is the one kind the
 * reader must ACT on and "Deprecated" states a fact about the feature more directly.
 * Changed anyway: the act-now signal is carried by the warning tone and the title
 * ("The legacy CSV import is going away on 1 October"), not by the participle, and one
 * odd label out of five costs more than that participle buys.
 */
export const ANNOUNCEMENT_KIND_LABELS: Record<AnnouncementKind, string> = {
  // 'Feature', not 'New'. A feature ANNOUNCEMENT and a feature REQUEST are the same
  // thing at two moments — the ask and the ship — and an announcement often closes a
  // request literally linked in its own "You asked for these" row. Naming them
  // differently made the reader do that mapping themselves.
  //
  // `fix` deliberately does NOT follow suit onto its support counterpart's word: that
  // counterpart is 'Bug', and "Bug" on an announcement reads as "we shipped a bug".
  // The colour carries the connection there instead (see KIND_TONE in
  // AnnouncementKindBadge).
  feature: 'Feature',
  improvement: 'Improvement',
  fix: 'Fix',
  security: 'Security',
  deprecation: 'Deprecation',
};

/** One board item an announcement closes, as shown on the card. */
export interface AnnouncementRequestedItem {
  boardItemId: string;
  title: string;
  /**
   * THE REQUEST this link should open — the thing the reader actually filed, not the
   * board item it was promoted onto.
   *
   * Resolved server-side rather than by the client, and the reason is that the client
   * cannot do it: it holds a BOARD ITEM id, the destination needs a REQUEST id, and the
   * join between them (`support_requests.board_item_id`, rolled up through
   * `merged_into_item_id` so a consolidated item still resolves) is not exposed to a
   * company caller in any other shape. Sending an extra id costs one correlated
   * subquery on a read that already runs; making the client fetch it would cost a round
   * trip per item on a line that usually has one.
   *
   * WHICH request, when a board item has several: the CALLER'S OWN, where one exists —
   * that is the one they asked about, and "you asked for this" pointing at a
   * colleague's thread is a worse answer than pointing at nothing. Falls back to the
   * company's earliest request for the item, and only for a caller who holds
   * `View company requests`; without that permission a colleague's request would 403
   * on arrival, so it is null instead.
   *
   * Null means "no request this reader may open" — a staff-authored board item, or a
   * colleague's request the reader cannot see. The card falls back to the request LIST
   * rather than rendering a dead link.
   */
  requestId: string | null;
}

/**
 * One announcement as a company user sees it.
 *
 * Note what is NOT here: `requiredPermission`. The audience gate is applied
 * SERVER-side — an announcement the caller may not see is not in the response at
 * all. Sending the gate to the client would mean the client could be trusted to
 * filter, and a permission string naming a feature is itself a small leak about
 * what exists. Staff get the field on `IAnnouncementInternal`, because staff set it.
 */
export interface IAnnouncement {
  id: string;
  title: string;
  body: string;
  kind: AnnouncementKind;
  /** ISO timestamp. Never null on this shape — drafts are filtered out server-side. */
  publishedAt: string;
  /** Present only when the announcement has a call to action. */
  ctaLabel: string | null;
  /**
   * Where the CTA goes. An in-app route (e.g. `/certificates`), never an external
   * URL — the point of the CTA is to land the user on the feature being announced.
   */
  ctaRoute: string | null;
  /**
   * If set, the CTA STARTS A PRODUCT TOUR instead of (or after) navigating. This is
   * the strong link between this surface and the tours system: an announcement can
   * hand the user straight into a guided walkthrough of the thing that just shipped.
   */
  tourKey: string | null;
  /** True once this user has dismissed it. Drives both the modal and the "New" dot. */
  seen: boolean;
  /**
   * The board items this announcement closed out, for the "you asked for this" line.
   *
   * Id AND title, because the card now LINKS each one back to the request it came
   * from — titles alone (the original shape) could only be printed, which left the
   * most meaningful line on the card as a dead end.
   *
   * Sending the id is safe for the same reason the titles were: this read is
   * company-scoped server-side, so it only ever contains items from the reader's OWN
   * board. The original note here worried that an id would leak another brand's
   * item; the scoping is what makes that impossible, not the absence of the field.
   */
  requested: AnnouncementRequestedItem[];
}

/** The company-facing feed plus the one thing the modal needs to decide anything. */
export interface IAnnouncementFeed {
  announcements: IAnnouncement[];
  /**
   * The newest announcement this user has not seen, or null.
   *
   * Computed SERVER-side rather than derived by the client from `announcements`,
   * and that is the whole "never show history as a queue" rule in one field: the
   * server has already applied the audience gate AND the "published after this
   * user's signup" cutoff, so there is exactly one candidate and the client cannot
   * accidentally loop over a backlog. At most one modal per session; the page
   * carries the rest.
   */
  unseenLatest: IAnnouncement | null;
  /** Cheap enough to compute here, and it is what the Help row's "New" dot reads. */
  unseenCount: number;
}

/**
 * Staff view. Carries everything the company shape hides: draft state, the audience
 * gate, and the linked board items as real ids so they can be unlinked.
 */
export interface IAnnouncementInternal {
  id: string;
  title: string;
  body: string;
  kind: AnnouncementKind;
  /** null = DRAFT. The timestamp is the state; there is no separate status field. */
  publishedAt: string | null;
  ctaLabel: string | null;
  ctaRoute: string | null;
  tourKey: string | null
  /**
   * LEGACY audience gate — a permission name; null = everyone.
   *
   * Superseded by `requiredRoleNames` (VRG-332) and no longer settable from the
   * editor, but STILL ENFORCED server-side for rows that carry one, ANDed with the
   * role gate. Sent to staff read-only so a gate that is still hiding an announcement
   * is visible and can be retired deliberately — translating it into an equivalent
   * role set is what the API refuses to guess at, because getting it wrong either
   * hides the announcement from people who could act on it or shows an
   * already-published gated announcement to people it was hidden from.
   */
  requiredPermission: string | null;
  /**
   * The audience gate: role NAMES. null (or empty) = everyone.
   *
   * NAMES rather than ids because an announcement is cross-company and a custom role's
   * id belongs to exactly one company (`company_custom_roles` is keyed
   * `unique(company_id, name)`). Built-in names resolve identically everywhere,
   * including for a company that overrode what a built-in role can do; a company's own
   * standalone custom role does not match, which is a stated, accepted limitation
   * rather than a bug — see migration 0074.
   *
   * Note this is on the INTERNAL shape only. `IAnnouncement` deliberately carries
   * neither gate: the filtering is server-side, and shipping the gate to the client
   * would imply the client could be trusted to apply it.
   */
  requiredRoleNames: string[] | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  /** Every board item this announcement closes out. */
  boardItems: IAnnouncementBoardItemLink[];
  /**
   * How many people the "Notify requesters" action would reach right now: every
   * voter on every linked item, plus the submitter of every support request that
   * points at one, de-duplicated. Shown BEFORE the action is taken, because a bulk
   * notification whose blast radius is invisible until after you click is how
   * someone emails the wrong 400 people.
   */
  notifyRecipientCount: number;
  /** Set once "Notify requesters" has run, so the action is not fired twice. */
  notifiedAt: string | null;
}

export interface IAnnouncementBoardItemLink {
  boardItemId: string;
  title: string;
  status: string;
  companyName: string | null;
  voteCount: number;
}

/** What "Notify requesters" actually did. Returned so the UI can be specific. */
export interface INotifyRequestersResult {
  /** Distinct users notified. */
  notified: number;
  /** Board items flipped to `shipped` by this call (already-shipped ones excluded). */
  itemsMarkedShipped: number;
  /** Support-request threads that got a system message. */
  threadsUpdated: number;
}
